import { useMatches, useRouteLoaderData } from 'react-router-dom';
import {
  TOGGLE_NAMES,
  toggleValuesSelector,
} from 'platform/utilities/feature-toggles/useFeatureToggle';
import manifest from '../manifest.json';
import { CLAIMANT_ID_PATTERN } from './constants';
import { HOME_LABEL, HelpBC } from './poaRequests';
import { toEmbedClaimant } from './quickSubmitEmbed';

const SUBMIT_FORMS_LABEL = 'Submit forms and files';

// A <Link to> takes a path inside the app. A plain href (a va-link, a
// breadcrumb) needs the basename, or it lands outside the app.
export const withBasename = path => `${manifest.rootUrl}${path}`;

// The other way: an href in the app back to a path for navigate(). Null for
// an href outside the app.
export const withoutBasename = href => {
  if (typeof href !== 'string' || !href.startsWith(manifest.rootUrl)) {
    return null;
  }
  const path = href.slice(manifest.rootUrl.length);
  if (path && !/^[/?#]/.test(path)) return null;
  return path.startsWith('/') ? path : `/${path}`;
};

export const QUICK_SUBMIT_BASE = '/submit-forms-and-files/upload';

export const QUICK_SUBMIT_ROUTE_ID = 'quick-submit';

// The values are the embed's own view names.
export const QUICK_SUBMIT_PAGES = Object.freeze({
  UPLOAD: 'uploadDocuments',
  EFORMS: 'eforms',
  CONFIRMATION: 'uploadConfirmation',
});

// The pages that show the embed. The embed's own links move between these
// without removing it, so an upload in progress keeps going.
export const QUICK_SUBMIT_EMBED_PAGES = Object.freeze([
  QUICK_SUBMIT_PAGES.UPLOAD,
  QUICK_SUBMIT_PAGES.EFORMS,
]);

// Each page's h1, title, and breadcrumb use this one label. No two labels are
// the same, so no two titles are the same.
export const QUICK_SUBMIT_LABELS = Object.freeze({
  [QUICK_SUBMIT_PAGES.UPLOAD]: 'Upload with QuickSubmit',
  [QUICK_SUBMIT_PAGES.EFORMS]: 'eForms',
  [QUICK_SUBMIT_PAGES.CONFIRMATION]: 'Submission started',
});

// Takes the h1 text, so an open eForm gets a title of its own.
export const quickSubmitTitle = heading =>
  `${heading} | Accredited Representative Portal | Veterans Affairs`;

// The embed's view while an eForm is open.
export const EFORM_VIEW = 'eformDisplay';

// An open eForm has its own URL, in the same two shapes as the embed's own
// routes. So a reload, a bookmark, Back, or a copied link opens the same form,
// or the same draft. These are child paths of QUICK_SUBMIT_BASE.
export const QUICK_SUBMIT_EFORM_NEW_PATH = 'eforms/:formId/new';
export const QUICK_SUBMIT_EFORM_EDIT_PATH = 'eforms/:formId/edit/:submissionId';

export const EFORM_STATE_KEY = 'eform';

// History state is whatever a past entry left there, so take only strings.
const idOrUndefined = value =>
  typeof value === 'string' || typeof value === 'number'
    ? `${value}`
    : undefined;

// The form's name, which only the embed knows. It rides in history state as a
// display hint, keyed by formId. A direct link has none, so the h1 falls back
// to generic copy.
export const eformFromState = state => {
  const eform = state?.[EFORM_STATE_KEY];
  const formId = idOrUndefined(eform?.formId);
  if (!formId) return null;
  return {
    formId,
    formNumber:
      typeof eform.formNumber === 'string' ? eform.formNumber : undefined,
    title: typeof eform.title === 'string' ? eform.title : undefined,
  };
};

export const eformState = ({ formId, formNumber, title }) => ({
  [EFORM_STATE_KEY]: {
    formId: idOrUndefined(formId),
    ...(formNumber && { formNumber }),
    ...(title && { title }),
  },
});

// The open eForm: its ids from the URL, its name from history state when that
// names the same form.
export const eformFromRoute = (params, state) => {
  const formId = idOrUndefined(params?.formId);
  if (!formId) return null;
  const hint = eformFromState(state);
  const name = hint?.formId === formId ? hint : {};
  return {
    formId,
    submissionId: idOrUndefined(params.submissionId),
    formNumber: name.formNumber,
    title: name.title,
  };
};

// The host owns the h1, since the embed's eForm screen has no title of its
// own. The name comes with the request, so it shows on the first frame.
export const eformHeading = eform => {
  if (eform.title && eform.formNumber) {
    return `${eform.title} (VA Form ${eform.formNumber})`;
  }
  if (eform.title) return eform.title;
  if (eform.formNumber) return `VA Form ${eform.formNumber}`;
  return eform.submissionId ? 'Edit eForm' : 'New eForm';
};

const eformCrumb = eform =>
  eform.formNumber ? `VA Form ${eform.formNumber}` : eformHeading(eform);

// Reached only once a packet is sent, so leaving for it never loses work.
export const QUICK_SUBMIT_CONFIRMATION_PATH = `${QUICK_SUBMIT_BASE}/confirmation`;

// Every in-section link carries the claimant, or prefill is lost on the
// first move between pages.
export const quickSubmitUrls = ({ claimantId } = {}) => {
  const query = claimantId
    ? `?claimantId=${encodeURIComponent(claimantId)}`
    : '';

  return {
    upload: `${QUICK_SUBMIT_BASE}${query}`,
    confirmation: `${QUICK_SUBMIT_CONFIRMATION_PATH}${query}`,
    eforms: `${QUICK_SUBMIT_BASE}/eforms${query}`,
    eform: ({ formId, submissionId }) => {
      const form = `${QUICK_SUBMIT_BASE}/eforms/${encodeURIComponent(formId)}`;
      return submissionId != null
        ? `${form}/edit/${encodeURIComponent(submissionId)}${query}`
        : `${form}/new${query}`;
    },
  };
};

export const QUICK_SUBMIT_BC_LABEL = 'quick submit breadcrumb';

export const quickSubmitBC = ({
  claimantId,
  page = QUICK_SUBMIT_PAGES.UPLOAD,
  eform = null,
} = {}) => {
  const urls = quickSubmitUrls({ claimantId });
  // A link to another page in the app is a router link, so a click is a route
  // change, not a page load. A page load would remove the embed and lose an
  // upload in progress, and the leave guard cannot ask first.
  const routerLinkCrumb = (path, label) => ({
    href: withBasename(path),
    label,
    isRouterLink: true,
  });
  const currentPageCrumb = label => ({ href: window.location.href, label });
  const uploadLabel = QUICK_SUBMIT_LABELS[QUICK_SUBMIT_PAGES.UPLOAD];

  const breadcrumbList = [
    routerLinkCrumb('', HOME_LABEL),
    routerLinkCrumb('/submit-forms-and-files', SUBMIT_FORMS_LABEL),
    page === QUICK_SUBMIT_PAGES.UPLOAD
      ? currentPageCrumb(uploadLabel)
      : routerLinkCrumb(urls.upload, uploadLabel),
  ];

  // The other pages sit one level under upload in the URL.
  if (page !== QUICK_SUBMIT_PAGES.UPLOAD) {
    breadcrumbList.push(
      eform
        ? routerLinkCrumb(urls.eforms, QUICK_SUBMIT_LABELS[page])
        : currentPageCrumb(QUICK_SUBMIT_LABELS[page]),
    );
  }

  // An open eForm sits one level under the list.
  if (eform) breadcrumbList.push(currentPageCrumb(eformCrumb(eform)));

  return breadcrumbList;
};

// A loader runs outside React, so it cannot use useToggleValue. It reads the
// store instead. The sign-in wrapper waits for the user fetch, and the flag
// fetch starts when that ends, so the flags are on their way by now. A failed
// flag fetch still ends the load, so this always settles.
export const waitForToggles = store =>
  new Promise(resolve => {
    let unsubscribe = null;
    const settle = () => {
      const toggles = toggleValuesSelector(store.getState());
      if (toggles.loading !== false) return false;
      if (unsubscribe) unsubscribe();
      resolve(toggles);
      return true;
    };
    if (!settle()) unsubscribe = store.subscribe(settle);
  });

// The help guide is a QuickSubmit page, so the same flag turns it on.
export const QUICK_SUBMIT_HELP_PATH = '/help/quicksubmit';
export const QUICK_SUBMIT_HELP_LABEL = 'Get help with QuickSubmit';
export const QUICK_SUBMIT_HELP_BC_LABEL = 'quick submit help breadcrumb';

// The page has no embed to keep, so plain links are fine here.
export const quickSubmitHelpBC = [
  ...HelpBC,
  {
    href: withBasename(QUICK_SUBMIT_HELP_PATH),
    label: QUICK_SUBMIT_HELP_LABEL,
  },
];

// The Veteran's full SSN, or null. vets-api asks VA Profile whether the
// claimant is a Veteran, and puts the record under veteranSubPage only when
// they are. Anyone else comes back under claimantSubPage, and their SSN must
// not land in the Veteran's file-number field. When VA Profile can't tell,
// vets-api says no. Any failure is null too, and the rep types the info in.
const fetchVeteranSsn = async (api, claimantId, signal) => {
  try {
    const prefillResponse = await api.getClaimantPrefill(claimantId, {
      signal,
    });
    if (!prefillResponse?.ok) return null;
    const prefillBody = await prefillResponse.json();
    return prefillBody?.formData?.veteranSubPage?.veteranSsn ?? null;
  } catch {
    return null;
  }
};

// The embed's prefill, or null so the rep types the Veteran's info in. Null
// when the claimant is not known to be the Veteran, or without the full SSN.
// The embed calls this while its session is minted, so the page never waits
// on it, and pages without the embed never make it.
export const fetchEmbedClaimant = async (
  api,
  { claimantId, overview, signal },
) => {
  if (!claimantId || !overview) return null;
  const ssn = await fetchVeteranSsn(api, claimantId, signal);
  return ssn ? toEmbedClaimant(overview, ssn) : null;
};

export const loadQuickSubmitClaimant =
  (api, store) =>
  async ({ request }) => {
    // Check the flag first, so a page that is off never calls the API.
    const toggles = await waitForToggles(store);
    if (!toggles[TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateQsLink]) {
      return { quickSubmitDisabled: true };
    }

    const claimantId = new URL(request.url).searchParams.get('claimantId');

    if (!claimantId) {
      return { claimantId: null, claimant: null, overview: null };
    }

    if (!CLAIMANT_ID_PATTERN.test(claimantId)) {
      return {
        claimantId,
        claimant: null,
        overview: null,
        notRepresented: true,
      };
    }

    try {
      // Only the overview holds up the page. The embed fetches its own
      // prefill (fetchEmbedClaimant).
      const overviewResponse = await api.getClaimantOverview(claimantId, {
        signal: request.signal,
      });

      // api.js resolves null on a 401 while it sends the browser to sign-in.
      // Hold the page until then, or it flashes up without the claimant.
      if (!overviewResponse) return new Promise(() => {});
      if (!overviewResponse.ok) throw overviewResponse;

      const overviewBody = await overviewResponse.json();
      const overview = overviewBody?.data || overviewBody;
      const { first_name: firstName, last_name: lastName } = overview;

      return {
        claimantId,
        claimant: { firstName, lastName },
        overview,
      };
    } catch (err) {
      if (!(err instanceof Response)) throw err;

      if (err.status === 403) {
        return {
          claimantId,
          claimant: null,
          overview: null,
          notRepresented: true,
        };
      }

      // vets-api also answers 404 when its identity lookup is down, so a
      // failure here does not mean the rep lacks access. They can still type
      // the details in.
      return {
        claimantId,
        claimant: null,
        overview: null,
        claimantUnavailable: true,
      };
    }
  };

export const shouldRevalidateQuickSubmit = ({ currentUrl, nextUrl }) =>
  currentUrl.searchParams.get('claimantId') !==
  nextUrl.searchParams.get('claimantId');

export const useQuickSubmitRouteLoaderData = () =>
  useRouteLoaderData(QUICK_SUBMIT_ROUTE_ID) || {};

// Each child route names its page in its handle, so the shared layout can
// draw that page's h1 and title.
export const quickSubmitHandle = page => ({ quickSubmitPage: page });

export const useQuickSubmitPage = () =>
  useMatches()
    .map(match => match.handle?.quickSubmitPage)
    .filter(Boolean)
    .pop() || QUICK_SUBMIT_PAGES.UPLOAD;
